import React, { useState } from "react";
import {
  ArrowRight,
  Maximize2,
  Mic,
  Minimize2,
  Paperclip,
  Send,
  Smile,
  X,
} from "lucide-react";
import { useChat } from "@/context/ChatProvider";
import { useNavigate } from "react-router-dom";

interface Message {
  id: string;
  content: string;
  sender: {
    id: string;
    name: string;
    avatar?: string;
  };
  timestamp: string;
  isOwn: boolean;
}

export default function ChatWindow() {
  const { selectedChat, closeChat } = useChat();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);

  const [messages] = useState<Message[]>([
    {
      id: "1",
      content: "Oi, como você está?",
      sender: {
        id: "2",
        name: selectedChat?.name || "",
        avatar: selectedChat?.avatar,
      },
      timestamp: "10:00",
      isOwn: false,
    },
    {
      id: "2",
      content: "Tudo bem! E você?",
      sender: {
        id: "1",
        name: "Você",
        avatar: "https://i.pravatar.cc/150?img=2",
      },
      timestamp: "10:01",
      isOwn: true,
    },
    {
      id: "3",
      content: "Vamos jogar uma partida?",
      sender: {
        id: "2",
        name: selectedChat?.name || "",
        avatar: selectedChat?.avatar,
      },
      timestamp: "10:02",
      isOwn: false,
    },
  ]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    // Aqui você implementaria a lógica para enviar a mensagem

    setMessage("");
  };

  const handleOpenChatPage = () => {
    navigate("/chat");
  };

  if (!selectedChat) return null;

  return (
    <div
      className={`fixed bottom-0 right-4 w-96 bg-[#1A1D23] rounded-t-lg shadow-xl transition-all duration-300 z-50 ${
        isMinimized ? "h-12" : "h-[600px]"
      }`}
    >
      {/* Header do Chat */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b border-gray-800 bg-[#2A2F3B] rounded-t-lg cursor-pointer"
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-8 h-8 rounded-full overflow-hidden">
              {selectedChat.avatar ? (
                <img
                  src={selectedChat.avatar}
                  alt={selectedChat.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-700 flex items-center justify-center text-gray-400 text-sm font-semibold">
                  {selectedChat.name[0]}
                </div>
              )}
            </div>
            {!selectedChat.isGroup && (
              <div
                className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border-2 border-[#1A1D23] ${
                  selectedChat.status === "online"
                    ? "bg-green-500"
                    : selectedChat.status === "away"
                      ? "bg-yellow-500"
                      : "bg-gray-500"
                }`}
              />
            )}
          </div>
          <div>
            <h2 className="text-white font-medium text-sm">
              {selectedChat.name}
            </h2>
            {!selectedChat.isGroup && (
              <p className="text-xs text-gray-400">
                {selectedChat.status === "online"
                  ? "Online"
                  : selectedChat.status === "away"
                    ? "Ausente"
                    : "Offline"}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenChatPage();
            }}
            className="p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowRight size={16} />
          </button>
          <button className="p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
            {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
          </button>
          <button className="p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
            <X
              size={16}
              onClick={(e) => {
                e.stopPropagation();
                closeChat();
              }}
            />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Área de Mensagens */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[calc(100%-120px)] scrollbar-custom">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.isOwn ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`flex items-end gap-2 max-w-[70%] ${msg.isOwn ? "flex-row-reverse" : "flex-row"}`}
                >
                  {!msg.isOwn && (
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                      {msg.sender.avatar ? (
                        <img
                          src={msg.sender.avatar}
                          alt={msg.sender.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center text-gray-400 text-sm font-semibold">
                          {msg.sender.name[0]}
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className={`flex flex-col ${msg.isOwn ? "items-end" : "items-start"}`}
                  >
                    <div
                      className={`rounded-lg px-4 py-2 ${
                        msg.isOwn
                          ? "bg-red-600 text-white"
                          : "bg-[#2A2F3B] text-white"
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <span className="text-xs opacity-70 mt-1 block">
                        {msg.timestamp}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Input de Mensagem */}
          <form
            onSubmit={handleSendMessage}
            className="p-4 border-t border-gray-800"
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Paperclip size={20} />
              </button>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Digite uma mensagem..."
                  className="w-full pl-4 pr-12 py-2 bg-[#2A2F3B] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <Smile size={20} />
                </button>
              </div>
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Mic size={20} />
              </button>
              <button
                type="submit"
                className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Send size={20} />
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}